import snapshot from '../data/payTransparency2026Snapshot.json';

type SnapshotRow = {
  id: number;
  source: string;
  rate_min: number;
  rate_max: number | null;
  rate_type: string;
  roles_needed: string[];
};

const rows = snapshot.rows as SnapshotRow[];

function median(values: number[]): number {
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

export function formatReportRate(value: number): string {
  if (Number.isInteger(value)) return `$${value.toFixed(0)}`;
  return `$${(Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2)}`;
}

function summarize(group: SnapshotRow[]) {
  return {
    count: group.length,
    lowestFloor: Math.min(...group.map(row => row.rate_min)),
    medianFloor: median(group.map(row => row.rate_min)),
    highestCeiling: Math.max(...group.map(row => row.rate_max ?? row.rate_min)),
  };
}

const bySource = Object.entries(
  rows.reduce<Record<string, number>>((counts, row) => {
    counts[row.source] = (counts[row.source] || 0) + 1;
    return counts;
  }, {}),
).sort((left, right) => right[1] - left[1]);

const roleLabels: Record<string, string> = {
  av_technician: 'AV technician',
  stagehand: 'Stagehand',
  rigger: 'Rigger',
  stage_manager: 'Stage manager',
  lighting_tech: 'Lighting technician',
};

const roleGroups = rows.reduce<Record<string, SnapshotRow[]>>((groups, row) => {
  const primaryRole = row.roles_needed[0] || 'unclassified';
  (groups[primaryRole] ||= []).push(row);
  return groups;
}, {});

export const PAY_TRANSPARENCY_REPORT = {
  snapshotAsOf: snapshot.snapshotAsOf,
  inputSha256: snapshot.inputSha256,
  totalListings: rows.length,
  cadenceCounts: Object.entries(rows.reduce<Record<string, number>>((counts, row) => {
    counts[row.rate_type] = (counts[row.rate_type] || 0) + 1;
    return counts;
  }, {})),
  sourceRows: bySource.map(([source, count]) => ({ source, count })),
  roleRows: Object.entries(roleGroups)
    .filter(([, group]) => group.length >= 5)
    .sort((left, right) => right[1].length - left[1].length)
    .map(([role, group]) => ({ role, label: roleLabels[role] || role, ...summarize(group) })),
  suppressedRoleGroups: Object.values(roleGroups).filter(group => group.length < 5).length,
  overall: summarize(rows),
};
